import {
  BoxGeometry,
  Color,
  DoubleSide,
  Group,
  LOD,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  ShaderMaterial,
  Texture,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

import {
  BOAT_PRESENTATION_CONFIG,
  NERIS_BOAT_DOCKS,
  nerisSurfaceElevationM,
} from "../../config/boats.ts";
import type { BoatPresentation } from "../../sim/boats/index.ts";

interface BoatAssetManifest {
  readonly schemaVersion: 1;
  readonly asset: {
    readonly id: string;
    readonly assetUrl: string;
    readonly lod1Url: string;
    readonly lodDistanceM: number;
  };
}

export interface BoatFleetDiagnostics {
  readonly vesselCount: number;
  readonly dockCount: number;
  readonly meshCountPerVessel: number;
}

export interface BoatFleetView {
  readonly object: Group;
  readonly diagnostics: BoatFleetDiagnostics;
  setPresentations(
    presentations: readonly Readonly<BoatPresentation>[],
  ): void;
  dispose(): void;
}

interface BoatVisual {
  readonly object: Group;
  readonly wake: Mesh<PlaneGeometry, ShaderMaterial>;
}

function createWakeMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    name: "electric-boat-wake",
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    uniforms: {
      wakeColour: { value: new Color(BOAT_PRESENTATION_CONFIG.wakeColour) },
      wakeStrength: { value: 0 },
      maximumOpacity: {
        value: BOAT_PRESENTATION_CONFIG.wakeMaximumOpacity,
      },
    },
    vertexShader: `
      varying vec2 vWakeUv;
      void main() {
        vWakeUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 wakeColour;
      uniform float wakeStrength;
      uniform float maximumOpacity;
      varying vec2 vWakeUv;
      void main() {
        float opening = mix(0.07, 0.34, vWakeUv.y);
        float leftArm = abs(vWakeUv.x - (0.5 - opening));
        float rightArm = abs(vWakeUv.x - (0.5 + opening));
        float arms = 1.0 - smoothstep(0.018, 0.075, min(leftArm, rightArm));
        float centre = (1.0 - smoothstep(0.035, 0.16, abs(vWakeUv.x - 0.5))) *
          (1.0 - vWakeUv.y) * 0.30;
        float lengthFade = smoothstep(0.0, 0.10, vWakeUv.y) *
          (1.0 - smoothstep(0.70, 1.0, vWakeUv.y));
        float alpha = min(1.0, arms + centre) * lengthFade *
          wakeStrength * maximumOpacity;
        gl_FragColor = vec4(wakeColour, alpha);
      }
    `,
  });
}

function requireManifest(value: unknown): BoatAssetManifest {
  if (
    typeof value !== "object" ||
    value === null ||
    !("schemaVersion" in value) ||
    value.schemaVersion !== 1 ||
    !("asset" in value) ||
    typeof value.asset !== "object" ||
    value.asset === null
  ) {
    throw new Error("Boat asset manifest must use schema version 1");
  }
  const asset = value.asset as Record<string, unknown>;
  if (
    typeof asset.id !== "string" ||
    typeof asset.assetUrl !== "string" ||
    typeof asset.lod1Url !== "string" ||
    typeof asset.lodDistanceM !== "number"
  ) {
    throw new Error("Boat asset manifest is incomplete");
  }
  return value as BoatAssetManifest;
}

function configureMeshes(object: Object3D, castShadow: boolean): number {
  let count = 0;
  object.traverse((child) => {
    if (child instanceof Mesh) {
      count += 1;
      child.castShadow = castShadow;
      child.receiveShadow = true;
    }
  });
  return count;
}

function createDockView(): Group {
  const group = new Group();
  group.name = "neris-electric-boat-docks";
  const platformMaterial = new MeshStandardMaterial({
    name: "boat-dock-timber",
    color: 0x5c4934,
    roughness: 0.94,
    metalness: 0,
  });
  const pileMaterial = new MeshStandardMaterial({
    name: "boat-dock-metal",
    color: 0x28302f,
    roughness: 0.7,
    metalness: 0.45,
  });
  for (const dock of NERIS_BOAT_DOCKS) {
    const deltaX = dock.berth.x - dock.exit.x;
    const deltaZ = dock.berth.z - dock.exit.z;
    const length = Math.max(3, Math.hypot(deltaX, deltaZ));
    const waterY = nerisSurfaceElevationM(dock.berth.x) + 0.42;
    const platform = new Mesh(
      new BoxGeometry(2.8, 0.20, length),
      platformMaterial,
    );
    platform.name = `boat-dock:${dock.id}`;
    platform.position.set(
      (dock.berth.x + dock.exit.x) / 2,
      waterY,
      (dock.berth.z + dock.exit.z) / 2,
    );
    platform.rotation.y = Math.atan2(deltaX, deltaZ);
    platform.castShadow = true;
    platform.receiveShadow = true;
    group.add(platform);
    for (const end of [-0.42, 0.42]) {
      for (const side of [-1, 1]) {
        const pile = new Mesh(
          new BoxGeometry(0.16, 2.4, 0.16),
          pileMaterial,
        );
        const alongX = Math.sin(platform.rotation.y) * length * end;
        const alongZ = Math.cos(platform.rotation.y) * length * end;
        const acrossX = Math.cos(platform.rotation.y) * side * 1.25;
        const acrossZ = -Math.sin(platform.rotation.y) * side * 1.25;
        pile.position.set(
          platform.position.x + alongX + acrossX,
          waterY + 0.25,
          platform.position.z + alongZ + acrossZ,
        );
        pile.castShadow = true;
        group.add(pile);
      }
    }
  }
  return group;
}

function disposeObject(object: Object3D): void {
  const geometries = new Set<{
    dispose(): void;
  }>();
  const materials = new Set<Material>();
  const textures = new Set<Texture>();
  object.traverse((child) => {
    if (!(child instanceof Mesh)) {
      return;
    }
    geometries.add(child.geometry);
    for (const material of Array.isArray(child.material)
      ? child.material
      : [child.material]) {
      materials.add(material);
      for (const value of Object.values(material)) {
        if (value instanceof Texture) {
          textures.add(value);
        }
      }
    }
  });
  for (const geometry of geometries) {
    geometry.dispose();
  }
  for (const texture of textures) {
    texture.dispose();
  }
  for (const material of materials) {
    material.dispose();
  }
  object.removeFromParent();
  object.clear();
}

export async function loadBoatFleetView(
  manifestUrl: string,
  fetcher: typeof fetch = fetch,
): Promise<BoatFleetView> {
  const response = await fetcher(manifestUrl);
  if (!response.ok) {
    throw new Error(`Could not load boat asset manifest (${response.status})`);
  }
  const manifest = requireManifest(await response.json());
  const loader = new GLTFLoader();
  const [full, lod1] = await Promise.all([
    loader.loadAsync(manifest.asset.assetUrl),
    loader.loadAsync(manifest.asset.lod1Url),
  ]);
  const meshCountPerVessel =
    configureMeshes(full.scene, true) + configureMeshes(lod1.scene, false);
  const object = new Group();
  object.name = "neris-electric-boat-fleet";
  object.add(createDockView());
  const visuals = new Map<string, BoatVisual>();
  const wakeGeometry = new PlaneGeometry(
    BOAT_PRESENTATION_CONFIG.wakeWidthM,
    BOAT_PRESENTATION_CONFIG.wakeLengthM,
  );
  wakeGeometry.rotateX(-Math.PI / 2);

  function createVisual(id: string): BoatVisual {
    const group = new Group();
    group.name = `boat:${id}`;
    const lod = new LOD();
    const fullClone = full.scene.clone(true);
    const lodClone = lod1.scene.clone(true);
    lod.addLevel(fullClone, 0);
    lod.addLevel(lodClone, manifest.asset.lodDistanceM, 0.1);
    // Blender +Y is exported as Three -Z; turn the source vessel so local +Z
    // remains the simulation's documented forward direction.
    lod.rotation.y = Math.PI;
    group.add(lod);
    const wake = new Mesh(wakeGeometry, createWakeMaterial());
    wake.name = `boat-wake:${id}`;
    wake.position.set(
      0,
      BOAT_PRESENTATION_CONFIG.wakeSurfaceLiftM,
      -BOAT_PRESENTATION_CONFIG.wakeOffsetBehindBoatM,
    );
    wake.visible = false;
    group.add(wake);
    object.add(group);
    const visual = { object: group, wake };
    visuals.set(id, visual);
    return visual;
  }

  let disposed = false;
  return {
    object,
    diagnostics: {
      vesselCount: NERIS_BOAT_DOCKS.length,
      dockCount: NERIS_BOAT_DOCKS.length,
      meshCountPerVessel,
    },
    setPresentations(presentations): void {
      if (disposed) {
        return;
      }
      const activeIds = new Set(presentations.map((item) => item.id));
      for (const [id, visual] of visuals) {
        visual.object.visible = activeIds.has(id);
      }
      for (const item of presentations) {
        const visual = visuals.get(item.id) ?? createVisual(item.id);
        visual.object.visible = true;
        visual.object.position.set(
          item.position.x,
          item.position.y,
          item.position.z,
        );
        visual.object.rotation.y = item.yawRadians;
        const speedFactor = Math.max(0, Math.min(
          1,
          (Math.abs(item.speedMps) -
            BOAT_PRESENTATION_CONFIG.wakeMinimumSpeedMps) /
            (BOAT_PRESENTATION_CONFIG.wakeFullStrengthSpeedMps -
              BOAT_PRESENTATION_CONFIG.wakeMinimumSpeedMps),
        ));
        visual.wake.visible = speedFactor > 0;
        const wakeStrength = visual.wake.material.uniforms.wakeStrength;
        if (wakeStrength !== undefined) {
          wakeStrength.value = speedFactor;
        }
        visual.wake.scale.set(0.72 + speedFactor * 0.48, 1, 1);
      }
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      disposeObject(object);
    },
  };
}
